import { Dictionary } from "@insite/client-framework/Common/Types";
import { setPostStyleGuideTheme, setPreStyleGuideTheme } from "@insite/client-framework/ThemeConfiguration";
import isCssCustomizer from "@toolkit/Framework/Common/Utilities/lodash/isCssCustomizer";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import parseNumber from "@toolkit/Framework/Common/Utilities/parseNumber";
import polyfillCss from "@toolkit/Framework/Common/Utilities/polyfillCss";
import { pxToRem, remToPx } from "@toolkit/Framework/Common/Utilities/unitHelper";
import Styleguide from "@toolkit/Framework/Types/Styleguide";
import baseStyleguide from "@toolkit/Themes/common";
import cloneDeepWith from "lodash/cloneDeepWith";
import last from "lodash/last";

export let themeCollection: Dictionary<Styleguide> = {};

export const setThemeCollection = (
    themes: Dictionary<Styleguide>,
    defaultThemeName: Toolkit.Theme.Name,
    type?: "pre" | "post",
) => {
    const builtThemes = getBuiltThemes(themes);
    const defaultTheme = builtThemes[defaultThemeName];

    if (defaultTheme) {
        if (type === "pre") {
            setPreStyleGuideTheme(defaultTheme);
        } else {
            setPostStyleGuideTheme(defaultTheme);
        }
    }

    themeCollection = builtThemes;

    return themeCollection;
};

const applyPolyfillCss = (baseObject: any) => {
    return cloneDeepWith(baseObject, (object: any, key: any) => {
        if (isCssCustomizer(object, key)) {
            return polyfillCss`${object}`;
        }
    });
};

const getBuiltThemes = (themes: Dictionary<Styleguide>) => {
    const builtThemes: Dictionary<Styleguide> = {};

    Object.entries(themes).forEach(([key, theme]) => {
        const build = mergeProps(baseStyleguide, { name: key, ...theme }) as Toolkit.Theme;

        // -- Layout

        const { spacing, contentWidth } = build.design.layout;

        const padding = last(spacing.side.filter(x => x)) || "0";
        const paddingPixel = parseNumber(padding.endsWith("rem") ? remToPx(padding) : padding) ?? 0;

        const siteWidth = contentWidth + paddingPixel * 2;
        const contentMaxWidths = spacing.side.map(x => (x ? `calc(100vw - (${x} * 2))` : null));
        contentMaxWidths.splice(-1, 1, pxToRem(contentWidth));

        build.design.layout.siteWidth = siteWidth;
        build.design.layout.contentMaxWidths = contentMaxWidths;

        // -- Breakpoints

        build.breakpoints.values = build.breakpoints.values.map(x => (x === -1 ? siteWidth : x));

        const maxWidth = build.breakpoints.values[build.breakpoints.values.length - 1];
        build.breakpoints.maxWidths = build.breakpoints.maxWidths.map(x => (x === -1 ? maxWidth : x));

        // -- Polyfills

        build.typography = applyPolyfillCss(build.typography);

        builtThemes[key] = build;
    });

    return builtThemes;
};
